import type {
  DynamicBooksEstimate,
  IntakeStatus,
  PublicBidDeliverySummary,
  PublicIntakeRequest,
  PublicIntakeResponse,
  PublicIntakeResponseData
} from "./validation";

export type PublicApiIssue = {
  path: string;
  code: string;
  message: string;
};

export type PublicApiErrorCode =
  | "validation_error"
  | "missing_consent"
  | "attachment_too_large"
  | "too_many_attachments"
  | "unsupported_attachment_type"
  | "payload_too_large"
  | "unauthorized"
  | "not_found"
  | "rate_limited"
  | "conflict"
  | "server_error"
  | "onboarding_token_required"
  | "invalid_onboarding_token"
  | "onboarding_token_not_found"
  | "onboarding_token_expired"
  | "onboarding_token_revoked"
  | "onboarding_context_unavailable"
  | "onboarding_token_mismatch"
  | "onboarding_already_submitted"
  | "invalid_onboarding_submission_payload"
  | "invalid_onboarding_tracking_payload"
  | "invalid_onboarding_upload_payload"
  | "proposal_onboarding_failed";

export class PublicApiError extends Error {
  readonly status: number;
  readonly code: PublicApiErrorCode;
  readonly issues: PublicApiIssue[];
  readonly retryAfterSeconds: number | null;

  constructor(status: number, code: PublicApiErrorCode, message: string, issues: PublicApiIssue[] = [], retryAfterSeconds: number | null = null) {
    super(message);
    this.name = "PublicApiError";
    this.status = status;
    this.code = code;
    this.issues = issues;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

type PublicApiErrorResponse = {
  ok: false;
  error: {
    code: PublicApiErrorCode;
    message: string;
    details?: {
      issues?: PublicApiIssue[];
      retry_after_seconds?: number;
    };
  };
};

export type CustomerLookupRequest = {
  business_email: string;
  registration_number?: string | null;
  vat_number?: string | null;
  case_public_id?: string | null;
};

export type CustomerLookupSummary = {
  case_public_id: string;
  case_display_id: string;
  status: IntakeStatus;
  created_at: string;
  updated_at: string;
  locale: "sv" | "en";
  requires_viewer_token: true;
};

export type CustomerLookupResponseData = {
  lookup_result: "match" | "no_match";
  cases: CustomerLookupSummary[];
};

export type SafeTimelineEvent = {
  event_public_id: string;
  event_name: string;
  created_at: string;
  payload: Record<string, string | number | boolean | null>;
};

export type PublicCaseData = {
  case_public_id: string;
  case_display_id: string;
  request_public_id: string;
  quote_public_id: string;
  bid_display_id: string;
  quote_run_public_id: string;
  locale: "sv" | "en";
  status: IntakeStatus;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
  customer_summary: {
    masked_email: string;
    country_code: string;
    legal_form: string;
    monthly_transaction_count: number;
    delivery_preference: "self_orchestrated_agent" | "human_assisted" | "hybrid";
  };
  estimate: DynamicBooksEstimate;
  bid_delivery: PublicBidDeliverySummary;
  accepted_attachments: Array<{
    attachment_public_id: string;
    filename: string;
    content_type: string;
    byte_size: number;
    kind: "sie_file" | "bank_statement" | "sample_invoice" | "supporting_material";
    sha256: string;
  }>;
  events: SafeTimelineEvent[];
  links: {
    case_path: string;
    status_path: string;
    stream_path: string;
  };
};

export type PublicJsonPrimitive = string | number | boolean | null;
export type PublicJsonValue = PublicJsonPrimitive | PublicJsonValue[] | { [key: string]: PublicJsonValue };
export type PublicJsonObject = { [key: string]: PublicJsonValue };

export type ProposalOnboardingSummary = {
  onboarding_public_id: string;
  status: "active" | "submitted" | "expired" | "revoked" | string;
  proposal_version: number;
  proposal_version_label: string;
  expires_at: string;
  last_loaded_at: string | null;
  submitted_at: string | null;
  token_present: true;
  raw_token_returned: false;
};

export type ProposalOnboardingCaseSummary = {
  case_public_id: string;
  case_display_id: string;
  title: string;
  locale: "sv" | "en";
  company_name: string;
  registration_number: string;
  masked_email: string;
  country_code: string;
  legal_form: string;
  company_information: {
    known_fields: {
      company_name: boolean;
      registration_number: boolean;
      vat_number: boolean;
      country_code?: boolean;
      contact_person: boolean;
      email: boolean;
      phone: boolean;
      billing_details: boolean;
    };
    prefill?: {
      company_name: string;
      registration_number: string;
      vat_number: string;
      country_code: string;
      country_display: string;
      contact_person: string;
      email: string;
      phone: string;
      billing_details: string;
      billing_instructions?: string;
    };
    missing_fields: string[];
    source: string;
  };
};

export type ProposalOnboardingQuoteSummary = {
  bid_job_public_id: string;
  proposal_id?: string | null;
  bid_display_id: string;
  bid_state: string;
  proposal_version: number;
  proposal_version_label: string;
};

export type ProposalOnboardingSelectedScope = {
  service_package: string | null;
  service_package_label?: string | null;
  delivery_preference: string | null;
  delivery_model_label?: string | null;
  first_month_total?: string | null;
  monthly_service_fee?: string | null;
  ongoing_monthly_fee_after_setup?: string | null;
  vat_included_pricing?: string | null;
  vat_period?: string | null;
  monthly_transaction_count?: number;
  monthly_sales_invoice_count?: number;
  monthly_supplier_invoice_count?: number;
  monthly_receipt_count?: number;
  needs_ap_ar_ledgers?: boolean;
  reporting_needs?: string[];
  selected_services?: string[];
  included_workstreams: string[];
  not_included: string[];
  proposal_version: number;
  proposal_version_label?: string;
  pricing_source: string;
};

export type ProposalOnboardingSourceSetup = {
  bank_sources: string[];
  bank_account_count: number;
  uses_sie_migration: boolean;
  payroll_employee_count: number;
  accepted_attachment_count: number;
};

export type ProposalOnboardingPdfArtifact = {
  available: true;
  label: string;
  display_name?: string;
  file_name: string;
  download_file_name?: string;
  mime_type: "application/pdf" | string;
  byte_size: number | null;
  url: string;
  url_present: true;
  sha256_present: boolean;
};

export type ProposalOnboardingAdaptiveFlags = {
  ask_company_information: boolean;
  show_sie_upload: boolean;
  show_payroll_questions: boolean;
  show_bank_connection: boolean;
  show_bank_file_upload: boolean;
  show_human_review_preferences: boolean;
};

export type ProposalOnboardingProgressSnapshot = {
  progress_public_id: string;
  case_public_id: string;
  bid_job_public_id: string;
  proposal_version: number;
  proposal_version_label: string;
  onboarding_public_id: string;
  status: "issued" | "viewed" | "accepted" | "onboarding_started" | "step_in_progress" | "submitted" | "completed" | string;
  current_step: string;
  completed_steps: string[];
  missing_items: string[];
  accepted_at: string | null;
  submitted_at: string | null;
  total_proposal_time_ms: number;
  total_onboarding_time_ms: number;
  step_time_ms: Record<string, number>;
  last_activity_at: string | null;
  raw_token_returned: false;
};

export type ProposalOnboardingModel = {
  response_version: 1;
  action: "proposal_onboarding_load";
  case_type: "SERVICEUPPSKATTNING";
  service_category: "SERVICEUPPSKATTNING";
  onboarding: ProposalOnboardingSummary;
  case: ProposalOnboardingCaseSummary;
  quote: ProposalOnboardingQuoteSummary;
  proposal: {
    selected_scope: ProposalOnboardingSelectedScope;
    pdf_artifact: ProposalOnboardingPdfArtifact | null;
    source_setup: ProposalOnboardingSourceSetup;
    confirmation_checkboxes_required: string[];
    adaptive_flags: ProposalOnboardingAdaptiveFlags;
  };
  progress: ProposalOnboardingProgressSnapshot;
  endpoints: {
    submit: string;
    events: string;
    progress: string;
  };
};

export type ProposalOnboardingSubmission = {
  submission_public_id: string;
  onboarding_public_id: string;
  case_public_id: string;
  bid_job_public_id: string;
  proposal_version: number;
  status: "submitted";
  submitted_at: string;
  selected_scope: PublicJsonObject;
  source_setup_choices: PublicJsonObject;
  confirmation_checkboxes: PublicJsonObject;
};

export type ProposalOnboardingSubmitData = Omit<ProposalOnboardingModel, "action"> & {
  action: "proposal_onboarding_submit";
  submission: ProposalOnboardingSubmission;
  timeline_event_public_id: string | null;
};

export type ProposalOnboardingUploadSectionKey =
  | "bank_files"
  | "supplier_invoices"
  | "sales_invoices"
  | "receipts"
  | "other_accounting_documents"
  | "sie_files"
  | "payroll_documents";

export type ProposalOnboardingUploadFileMetadata = {
  file_name: string;
  mime_type: string;
  byte_size: number;
  last_modified: number | null;
};

export type ProposalOnboardingUploadMetadata = {
  section_key: ProposalOnboardingUploadSectionKey;
  section_title: string;
  file_count: number;
  files: ProposalOnboardingUploadFileMetadata[];
  status: "metadata_recorded";
  recorded_at: string;
};

export type ProposalOnboardingUploadMetadataRequest = {
  case_type: "SERVICEUPPSKATTNING";
  service_category: "SERVICEUPPSKATTNING";
  section_key: ProposalOnboardingUploadSectionKey;
  section_title: string;
  files: ProposalOnboardingUploadFileMetadata[];
  metadata_json?: PublicJsonObject;
};

export type ProposalOnboardingUploadMetadataData = {
  response_version: 1;
  action: "proposal_onboarding_upload_metadata";
  case_type: "SERVICEUPPSKATTNING";
  service_category: "SERVICEUPPSKATTNING";
  onboarding_public_id: string;
  raw_token_returned: false;
  upload: ProposalOnboardingUploadMetadata;
  upload_counts: Partial<Record<ProposalOnboardingUploadSectionKey, number>>;
};

export type ProposalOnboardingTrackEventType =
  | "proposal_viewed"
  | "proposal_accepted"
  | "onboarding_started"
  | "step_entered"
  | "step_completed"
  | "missing_items_updated"
  | "submitted"
  | "completed"
  | "onboarding_submitted"
  | "onboarding_completed";

export type ProposalOnboardingTrackEventRequest = {
  case_type: "SERVICEUPPSKATTNING";
  service_category: "SERVICEUPPSKATTNING";
  event_type: ProposalOnboardingTrackEventType;
  client_event_id?: string;
  step_key?: string | null;
  next_step_key?: string | null;
  aggregate_time_ms?: number | null;
  missing_items?: string[];
  occurred_at?: string | null;
  metadata_json?: PublicJsonObject;
};

export type ProposalOnboardingTrackEventData = {
  response_version: 1;
  action: "proposal_onboarding_track_event";
  case_type: "SERVICEUPPSKATTNING";
  service_category: "SERVICEUPPSKATTNING";
  idempotent_replay: boolean;
  event: {
    event_public_id: string;
    progress_public_id: string;
    event_type: string;
    step_key: string | null;
    aggregate_time_ms: number | null;
    status_after: string | null;
    occurred_at: string | null;
    recorded_at: string;
    client_event_id_present: boolean;
    raw_client_event_id_returned: false;
  };
  progress: ProposalOnboardingProgressSnapshot;
};

export type ProposalOnboardingTrackEventOptions = {
  keepalive?: boolean;
};

export type ProposalOnboardingProgressData = {
  response_version: 1;
  action: "proposal_onboarding_progress";
  case_type: "SERVICEUPPSKATTNING";
  service_category: "SERVICEUPPSKATTNING";
  progress: ProposalOnboardingProgressSnapshot;
};

export type ProposalOnboardingSubmitRequest = {
  case_type: "SERVICEUPPSKATTNING";
  service_category: "SERVICEUPPSKATTNING";
  case_public_id: string;
  bid_job_public_id: string;
  proposal_version: number;
  selected_scope_json: PublicJsonObject;
  source_setup_choices_json: PublicJsonObject;
  confirmation_checkboxes_json: PublicJsonObject;
  status: "submitted";
  metadata_json?: PublicJsonObject;
};

type SessionStorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const viewerTokenStoragePrefix = "dynamicbooks.case.";
const viewerTokenStorageSuffix = ".viewer";

export function getViewerTokenStorageKey(caseId: string): string {
  return `${viewerTokenStoragePrefix}${caseId}${viewerTokenStorageSuffix}`;
}

export function storeCaseViewerToken(caseId: string, viewerToken: string, storage = getBrowserSessionStorage()): boolean {
  const safeCaseId = caseId.trim();
  const safeViewerToken = viewerToken.trim();

  if (!storage || safeCaseId.length === 0 || safeViewerToken.length === 0) {
    return false;
  }

  try {
    storage.setItem(getViewerTokenStorageKey(safeCaseId), safeViewerToken);
    return true;
  } catch {
    return false;
  }
}

export function readCaseViewerToken(caseId: string, storage = getBrowserSessionStorage()): string | null {
  const safeCaseId = caseId.trim();

  if (!storage || safeCaseId.length === 0) {
    return null;
  }

  try {
    return storage.getItem(getViewerTokenStorageKey(safeCaseId));
  } catch {
    return null;
  }
}

export function clearCaseViewerToken(caseId: string, storage = getBrowserSessionStorage()): boolean {
  const safeCaseId = caseId.trim();

  if (!storage || safeCaseId.length === 0) {
    return false;
  }

  try {
    storage.removeItem(getViewerTokenStorageKey(safeCaseId));
    return true;
  } catch {
    return false;
  }
}

export function buildCustomerLookupRequest(input: {
  businessEmail: string;
  identifier: string;
  casePublicId?: string | null;
}): CustomerLookupRequest {
  const businessEmail = input.businessEmail.trim().toLowerCase();
  const identifier = input.identifier.trim();
  const normalizedIdentifier = identifier.replace(/[^a-z0-9]/gi, "").toUpperCase();
  const casePublicId = input.casePublicId?.trim() || null;
  const isVatIdentifier = normalizedIdentifier.startsWith("SE");

  return {
    business_email: businessEmail,
    registration_number: normalizedIdentifier && !isVatIdentifier ? identifier : null,
    vat_number: normalizedIdentifier && isVatIdentifier ? identifier : null,
    case_public_id: casePublicId
  };
}

export async function submitPublicIntake(
  request: PublicIntakeRequest,
  endpoint = "/api/public/intake"
): Promise<PublicIntakeResponseData> {
  const response = await fetch(endpoint, {
    method: "POST",
    credentials: "omit",
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": createPublicIntakeIdempotencyKey()
    },
    body: JSON.stringify(request)
  });

  const payload = await readJson(response);

  if (!response.ok || !isPublicIntakeResponse(payload)) {
    throw createPublicApiError(response.status, payload);
  }

  return payload.data;
}

function createPublicIntakeIdempotencyKey(): string {
  const randomId =
    typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  return `dbi_${randomId}`;
}

export async function lookupPublicCases(
  request: CustomerLookupRequest,
  endpoint = "/api/public/customer-lookup"
): Promise<CustomerLookupResponseData> {
  const response = await fetch(endpoint, {
    method: "POST",
    credentials: "omit",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(request)
  });

  const payload = await readJson(response);

  if (!response.ok || !isCustomerLookupResponse(payload)) {
    throw createPublicApiError(response.status, payload);
  }

  return payload.data;
}

export async function getPublicCase(caseId: string, viewerToken: string, endpointPrefix = "/api/public/cases"): Promise<PublicCaseData> {
  const response = await fetch(`${endpointPrefix}/${encodeURIComponent(caseId)}`, {
    method: "GET",
    credentials: "omit",
    headers: {
      "x-dynamicbooks-viewer-token": viewerToken
    }
  });

  const payload = await readJson(response);

  if (!response.ok || !isPublicCaseResponse(payload)) {
    throw createPublicApiError(response.status, payload);
  }

  return payload.data;
}

export async function loadProposalOnboarding(
  onboardingToken: string,
  endpointPrefix = "/api/public/proposal-onboarding"
): Promise<ProposalOnboardingModel> {
  const response = await fetch(`${normalizeEndpointPrefix(endpointPrefix)}/${encodeURIComponent(onboardingToken.trim())}`, {
    method: "GET",
    credentials: "omit",
    headers: {
      "Accept": "application/json"
    }
  });

  const payload = await readJson(response);

  if (!response.ok || !isProposalOnboardingLoadResponse(payload)) {
    throw createPublicApiError(response.status, payload);
  }

  return payload.data;
}

export async function submitProposalOnboarding(
  onboardingToken: string,
  request: ProposalOnboardingSubmitRequest,
  endpointPrefix = "/api/public/proposal-onboarding"
): Promise<ProposalOnboardingSubmitData> {
  const response = await fetch(`${normalizeEndpointPrefix(endpointPrefix)}/${encodeURIComponent(onboardingToken.trim())}/submit`, {
    method: "POST",
    credentials: "omit",
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
      "Idempotency-Key": createProposalOnboardingIdempotencyKey()
    },
    body: JSON.stringify(request)
  });

  const payload = await readJson(response);

  if (!response.ok || !isProposalOnboardingSubmitResponse(payload)) {
    throw createPublicApiError(response.status, payload);
  }

  return payload.data;
}

export async function recordProposalOnboardingUploadMetadata(
  onboardingToken: string,
  request: ProposalOnboardingUploadMetadataRequest,
  endpointPrefix = "/api/public/proposal-onboarding"
): Promise<ProposalOnboardingUploadMetadataData> {
  const response = await fetch(`${normalizeEndpointPrefix(endpointPrefix)}/${encodeURIComponent(onboardingToken.trim())}/uploads`, {
    method: "POST",
    credentials: "omit",
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
      "Idempotency-Key": createProposalOnboardingIdempotencyKey()
    },
    body: JSON.stringify(request)
  });

  const payload = await readJson(response);

  if (!response.ok || !isProposalOnboardingUploadMetadataResponse(payload)) {
    throw createPublicApiError(response.status, payload);
  }

  return payload.data;
}

export async function loadProposalOnboardingProgress(
  onboardingToken: string,
  endpointPrefix = "/api/public/proposal-onboarding"
): Promise<ProposalOnboardingProgressData> {
  const response = await fetch(`${normalizeEndpointPrefix(endpointPrefix)}/${encodeURIComponent(onboardingToken.trim())}/progress`, {
    method: "GET",
    credentials: "omit",
    headers: {
      "Accept": "application/json"
    }
  });

  const payload = await readJson(response);

  if (!response.ok || !isProposalOnboardingProgressResponse(payload)) {
    throw createPublicApiError(response.status, payload);
  }

  return payload.data;
}

export async function trackProposalOnboardingEvent(
  onboardingToken: string,
  request: ProposalOnboardingTrackEventRequest,
  endpointPrefix = "/api/public/proposal-onboarding",
  options: ProposalOnboardingTrackEventOptions = {}
): Promise<ProposalOnboardingTrackEventData> {
  const response = await fetch(`${normalizeEndpointPrefix(endpointPrefix)}/${encodeURIComponent(onboardingToken.trim())}/events`, {
    method: "POST",
    credentials: "omit",
    keepalive: options.keepalive === true ? true : undefined,
    headers: {
      "Accept": "application/json",
      "Content-Type": "application/json",
      "Idempotency-Key": request.client_event_id ?? createProposalOnboardingEventId()
    },
    body: JSON.stringify(request)
  });

  const payload = await readJson(response);

  if (!response.ok || !isProposalOnboardingTrackEventResponse(payload)) {
    throw createPublicApiError(response.status, payload);
  }

  return payload.data;
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) {
    return null;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function isPublicIntakeResponse(payload: unknown): payload is PublicIntakeResponse {
  if (!isRecord(payload) || payload.ok !== true || !isRecord(payload.data)) {
    return false;
  }

  return (
    typeof payload.data.case_public_id === "string" &&
    typeof payload.data.viewer_token === "string" &&
    typeof payload.data.status === "string" &&
    typeof payload.data.bid_status === "string" &&
    isRecord(payload.data.bid_delivery) &&
    isRecord(payload.data.estimate)
  );
}

function isCustomerLookupResponse(payload: unknown): payload is { ok: true; data: CustomerLookupResponseData } {
  if (!isRecord(payload) || payload.ok !== true || !isRecord(payload.data)) {
    return false;
  }

  return (
    (payload.data.lookup_result === "match" || payload.data.lookup_result === "no_match") &&
    Array.isArray(payload.data.cases)
  );
}

function isPublicCaseResponse(payload: unknown): payload is { ok: true; data: PublicCaseData } {
  if (!isRecord(payload) || payload.ok !== true || !isRecord(payload.data)) {
    return false;
  }

  return (
    typeof payload.data.case_public_id === "string" &&
    typeof payload.data.status === "string" &&
    isRecord(payload.data.estimate) &&
    isRecord(payload.data.bid_delivery) &&
    Array.isArray(payload.data.events)
  );
}

function isProposalOnboardingLoadResponse(payload: unknown): payload is { ok: true; data: ProposalOnboardingModel } {
  if (!isRecord(payload) || payload.ok !== true || !isRecord(payload.data)) {
    return false;
  }

  return isProposalOnboardingModel(payload.data) && payload.data.action === "proposal_onboarding_load";
}

function isProposalOnboardingSubmitResponse(payload: unknown): payload is { ok: true; data: ProposalOnboardingSubmitData } {
  if (!isRecord(payload) || payload.ok !== true || !isRecord(payload.data)) {
    return false;
  }

  return (
    isProposalOnboardingModel(payload.data) &&
    payload.data.action === "proposal_onboarding_submit" &&
    isRecord(payload.data.submission) &&
    typeof payload.data.submission.submission_public_id === "string"
  );
}

function isProposalOnboardingProgressResponse(payload: unknown): payload is { ok: true; data: ProposalOnboardingProgressData } {
  if (!isRecord(payload) || payload.ok !== true || !isRecord(payload.data)) {
    return false;
  }

  return (
    payload.data.response_version === 1 &&
    payload.data.action === "proposal_onboarding_progress" &&
    payload.data.case_type === "SERVICEUPPSKATTNING" &&
    payload.data.service_category === "SERVICEUPPSKATTNING" &&
    isProposalOnboardingProgressSnapshot(payload.data.progress)
  );
}

function isProposalOnboardingTrackEventResponse(payload: unknown): payload is { ok: true; data: ProposalOnboardingTrackEventData } {
  if (!isRecord(payload) || payload.ok !== true || !isRecord(payload.data)) {
    return false;
  }

  return (
    payload.data.response_version === 1 &&
    payload.data.action === "proposal_onboarding_track_event" &&
    payload.data.case_type === "SERVICEUPPSKATTNING" &&
    payload.data.service_category === "SERVICEUPPSKATTNING" &&
    typeof payload.data.idempotent_replay === "boolean" &&
    isRecord(payload.data.event) &&
    typeof payload.data.event.event_public_id === "string" &&
    payload.data.event.raw_client_event_id_returned === false &&
    isProposalOnboardingProgressSnapshot(payload.data.progress)
  );
}

function isProposalOnboardingUploadMetadataResponse(payload: unknown): payload is { ok: true; data: ProposalOnboardingUploadMetadataData } {
  if (!isRecord(payload) || payload.ok !== true || !isRecord(payload.data)) {
    return false;
  }

  return (
    payload.data.response_version === 1 &&
    payload.data.action === "proposal_onboarding_upload_metadata" &&
    payload.data.case_type === "SERVICEUPPSKATTNING" &&
    payload.data.service_category === "SERVICEUPPSKATTNING" &&
    typeof payload.data.onboarding_public_id === "string" &&
    payload.data.raw_token_returned === false &&
    isRecord(payload.data.upload) &&
    typeof payload.data.upload.section_key === "string" &&
    typeof payload.data.upload.file_count === "number" &&
    Array.isArray(payload.data.upload.files) &&
    isRecord(payload.data.upload_counts)
  );
}

function isProposalOnboardingModel(value: unknown): value is ProposalOnboardingModel | ProposalOnboardingSubmitData {
  if (!isRecord(value) || !isRecord(value.onboarding) || !isRecord(value.case) || !isRecord(value.quote) || !isRecord(value.proposal)) {
    return false;
  }

  return (
    value.response_version === 1 &&
    value.case_type === "SERVICEUPPSKATTNING" &&
    value.service_category === "SERVICEUPPSKATTNING" &&
    typeof value.onboarding.onboarding_public_id === "string" &&
    typeof value.onboarding.raw_token_returned === "boolean" &&
    value.onboarding.raw_token_returned === false &&
    typeof value.case.case_public_id === "string" &&
    typeof value.case.locale === "string" &&
    typeof value.quote.bid_job_public_id === "string" &&
    isRecord(value.proposal.selected_scope) &&
    isRecord(value.proposal.source_setup) &&
    isRecord(value.proposal.adaptive_flags) &&
    Array.isArray(value.proposal.confirmation_checkboxes_required) &&
    isProposalOnboardingProgressSnapshot(value.progress)
  );
}

function isProposalOnboardingProgressSnapshot(value: unknown): value is ProposalOnboardingProgressSnapshot {
  return (
    isRecord(value) &&
    typeof value.progress_public_id === "string" &&
    typeof value.onboarding_public_id === "string" &&
    typeof value.status === "string" &&
    typeof value.total_proposal_time_ms === "number" &&
    typeof value.total_onboarding_time_ms === "number" &&
    Array.isArray(value.completed_steps) &&
    Array.isArray(value.missing_items) &&
    isRecord(value.step_time_ms) &&
    value.raw_token_returned === false
  );
}

function createPublicApiError(status: number, payload: unknown): PublicApiError {
  if (isPublicApiErrorResponse(payload)) {
    return new PublicApiError(
      status,
      payload.error.code,
      toPublicSafeApiErrorMessage(payload.error.code, payload.error.message),
      payload.error.details?.issues ?? [],
      payload.error.details?.retry_after_seconds ?? null
    );
  }

  return new PublicApiError(status, "server_error", "The estimate service did not return the expected response.");
}

function toPublicSafeApiErrorMessage(code: PublicApiErrorCode, fallbackMessage: string): string {
  switch (code) {
    case "onboarding_token_required":
      return "Open the onboarding link from your proposal email.";
    case "invalid_onboarding_token":
    case "onboarding_token_not_found":
      return "This onboarding link is not valid. Request a new proposal link from DynamicBooks.";
    case "onboarding_token_expired":
      return "This onboarding link has expired. Request a new proposal link from DynamicBooks.";
    case "onboarding_token_revoked":
      return "This onboarding link is no longer active. Request a new proposal link from DynamicBooks.";
    case "onboarding_context_unavailable":
    case "onboarding_token_mismatch":
      return "We could not open this onboarding link. Request a new proposal link from DynamicBooks.";
    case "onboarding_already_submitted":
      return "This onboarding request has already been submitted.";
    case "invalid_onboarding_submission_payload":
      return "Review the highlighted onboarding details and try again.";
    case "invalid_onboarding_tracking_payload":
      return "We could not save onboarding progress for this step.";
    case "invalid_onboarding_upload_payload":
      return "Review the selected document details and try again.";
    case "proposal_onboarding_failed":
      return isPublicSafeFallbackMessage(fallbackMessage)
        ? fallbackMessage
        : "We could not open this onboarding link. Request a new proposal link from DynamicBooks.";
    default:
      return fallbackMessage;
  }
}

function isPublicSafeFallbackMessage(message: string): boolean {
  const normalized = String(message ?? "").trim();
  return normalized.length > 0
    && !/\btoken\b/i.test(normalized)
    && !/\b(cookie|credential|authorization|session)\b/i.test(normalized)
    && !/failed cleanly/i.test(normalized);
}

function isPublicApiErrorResponse(payload: unknown): payload is PublicApiErrorResponse {
  if (!isRecord(payload) || payload.ok !== false || !isRecord(payload.error)) {
    return false;
  }

  return typeof payload.error.code === "string" && typeof payload.error.message === "string";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function normalizeEndpointPrefix(endpointPrefix: string): string {
  return endpointPrefix.replace(/\/+$/, "");
}

function createProposalOnboardingIdempotencyKey(): string {
  const randomId =
    typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  return `dbon_submit_${randomId}`;
}

function createProposalOnboardingEventId(): string {
  const randomId =
    typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  return `dbon_evt_${randomId}`;
}

function getBrowserSessionStorage(): SessionStorageLike | null {
  if (typeof window === "undefined") {
    return null;
  }

  return window.sessionStorage;
}
